import { useEffect, useMemo, useRef, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { loadBringScript } from '@/lib/bring-widget'

interface BringCartImportProps {
  recipeIds: string[]
}

export function BringCartImport({ recipeIds }: BringCartImportProps) {
  const widgetRef = useRef<HTMLDivElement>(null)
  const [isScriptReady, setIsScriptReady] = useState<boolean>(
    Boolean(window.bringwidgets?.import?.render)
  )

  const cartImportUrl = useMemo(() => {
    if (typeof window === 'undefined' || !recipeIds.length) {
      return ''
    }

    const endpoint = new URL('/api/bring/cart', window.location.origin)
    endpoint.searchParams.set('ids', recipeIds.join(','))

    return endpoint.toString()
  }, [recipeIds])

  useEffect(() => {
    if (!cartImportUrl || !widgetRef.current) {
      return
    }

    void loadBringScript()
      .then(() => {
        setIsScriptReady(true)
        if (!widgetRef.current || !window.bringwidgets?.import?.render) {
          return
        }

        widgetRef.current.innerHTML = ''
        window.bringwidgets.import.render(widgetRef.current, {
          url: cartImportUrl,
          language: 'en',
          theme: 'light',
        })
      })
      .catch(() => {
        setIsScriptReady(false)
      })
  }, [cartImportUrl])

  if (!recipeIds.length) {
    return null
  }

  return (
    <div className="space-y-2">
      {!isScriptReady ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
          Bring-knop laden...
        </div>
      ) : null}

      <div ref={widgetRef} className="min-h-10" />

      <Button asChild variant="outline" size="sm" className="w-full">
        <a href={cartImportUrl} target="_blank" rel="noreferrer">
          <ExternalLink className="mr-2 h-4 w-4" />
          Open gecombineerde boodschappenlijst
        </a>
      </Button>
    </div>
  )
}
